import React from 'react'
import { Form } from 'semantic-ui-react'
import {
    FormTextFormField,
    FormDropdownField,
    FormCheckBoxField,
    AppPrimaryButton,
} from 'elements'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { FlexRowBetween, PageHeader, FlexRow } from 'components'
import { DesktopMode, MobileMode } from 'layout'
import { CaretLeft } from 'phosphor-react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

const SearchFormSchema = yup.object().shape({
    Keyword: yup.string(),
    Category: yup.string(),
    Location: yup.string(),
    StatusActive: yup.boolean(),
    StatusUnderMaintenance: yup.boolean(),
    StatusInactive: yup.boolean(),
    StatusDisposed: yup.boolean(),
})

const SearchCriteriaSection = styled.div`
    background: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 20px;
    margin-bottom: 20px;
`

const SearchResultsSection = styled.div`
    background: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 20px;
    min-height: 300px;
`

const SectionTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #333;
`

const StatusGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
`

export const AssetSearchFormAdvanced = ({
    onSearch,
    onNewSearch,
    onExport,
    locationOptions,
    categoryOptions,
    searchResults,
    loading,
}) => {
    const navigate = useNavigate()
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(SearchFormSchema),
        defaultValues: {
            Keyword: '',
            Category: '',
            Location: '',
            StatusActive: false,
            StatusUnderMaintenance: false,
            StatusInactive: false,
            StatusDisposed: false,
        },
    })

    const submit = async (formData) => {
        const statusArray = []
        if (formData.StatusActive) statusArray.push('Active')
        if (formData.StatusUnderMaintenance) statusArray.push('Under Maintenance')
        if (formData.StatusInactive) statusArray.push('Inactive')
        if (formData.StatusDisposed) statusArray.push('Disposed')

        const searchParams = {
            Keyword: formData.Keyword || '',
            Category: formData.Category || '',
            Location: formData.Location || '',
            Status: statusArray,
        }

        if (onSearch) {
            onSearch(searchParams)
        }
    }

    const handleNewSearch = () => {
        reset({
            Keyword: '',
            Category: '',
            Location: '',
            StatusActive: false,
            StatusUnderMaintenance: false,
            StatusInactive: false,
            StatusDisposed: false,
        })
        if (onNewSearch) {
            onNewSearch()
        }
    }

    return (
        <>
            <DesktopMode>
                <FlexRowBetween className="mb-3">
                    <PageHeader header="Asset Search" />
                    <AppPrimaryButton onClick={handleNewSearch}>
                        New Search
                    </AppPrimaryButton>
                </FlexRowBetween>

                <SearchCriteriaSection>
                    <SectionTitle>Search Criteria</SectionTitle>
                    <Form noValidate>
                        <Form.Group>
                            <FormTextFormField
                                control={control}
                                name="Keyword"
                                label="Keyword"
                                placeholder="Enter keyword"
                                errors={errors.Keyword}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <FormDropdownField
                                control={control}
                                name="Category"
                                label="Category"
                                placeholder="Select Category"
                                errors={errors.Category}
                                options={categoryOptions}
                            />
                            <FormDropdownField
                                control={control}
                                name="Location"
                                label="Location"
                                placeholder="Select Location"
                                errors={errors.Location}
                                options={locationOptions}
                            />
                        </Form.Group>
                        <Form.Group>
                            <label style={{ fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                                Status
                            </label>
                            <StatusGroup>
                                <FormCheckBoxField
                                    control={control}
                                    name="StatusActive"
                                    label="Active"
                                    errors={errors.StatusActive}
                                />
                                <FormCheckBoxField
                                    control={control}
                                    name="StatusUnderMaintenance"
                                    label="Under Maintenance"
                                    errors={errors.StatusUnderMaintenance}
                                />
                                <FormCheckBoxField
                                    control={control}
                                    name="StatusInactive"
                                    label="Inactive"
                                    errors={errors.StatusInactive}
                                />
                                <FormCheckBoxField
                                    control={control}
                                    name="StatusDisposed"
                                    label="Disposed"
                                    errors={errors.StatusDisposed}
                                />
                            </StatusGroup>
                        </Form.Group>
                        <div className="mt-3">
                            <AppPrimaryButton
                                onClick={handleSubmit(submit)}
                                loading={loading}
                            >
                                Search
                            </AppPrimaryButton>
                        </div>
                    </Form>
                </SearchCriteriaSection>

                <SearchResultsSection>
                    <FlexRowBetween className="mb-3">
                        <SectionTitle>Search Results</SectionTitle>
                        {searchResults && searchResults.length > 0 && (
                            <AppPrimaryButton onClick={onExport}>
                                Export
                            </AppPrimaryButton>
                        )}
                    </FlexRowBetween>
                    {loading && <div>Loading...</div>}
                    {!loading && (!searchResults || searchResults.length === 0) && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            No results found
                        </div>
                    )}
                    {!loading && searchResults && searchResults.length > 0 && (
                        <div>
                            {/* Results will be displayed here */}
                            <p>Found {searchResults.length} results</p>
                        </div>
                    )}
                </SearchResultsSection>
            </DesktopMode>

            <MobileMode>
                <div className="mb-3">
                    <FlexRowBetween className="mb-3">
                        <FlexRow
                            onClick={() => navigate('/app/asset-management')}
                            className="align-items-center pointer"
                        >
                            <CaretLeft size={24} />
                        </FlexRow>
                        <AppPrimaryButton
                            onClick={handleSubmit(submit)}
                            loading={loading}
                        >
                            Search
                        </AppPrimaryButton>
                    </FlexRowBetween>

                    <h2 style={{ marginBottom: '8px', fontWeight: 600 }}>
                        Asset Search
                    </h2>

                    <SearchCriteriaSection>
                        <SectionTitle>Search Criteria</SectionTitle>
                        <Form noValidate>
                            <Form.Group>
                                <FormTextFormField
                                    control={control}
                                    name="Keyword"
                                    label="Keyword"
                                    placeholder="Enter keyword"
                                    errors={errors.Keyword}
                                />
                            </Form.Group>
                            <Form.Group>
                                <FormDropdownField
                                    control={control}
                                    name="Category"
                                    label="Category"
                                    placeholder="Select Category"
                                    errors={errors.Category}
                                    options={categoryOptions}
                                />
                            </Form.Group>
                            <Form.Group>
                                <FormDropdownField
                                    control={control}
                                    name="Location"
                                    label="Location"
                                    placeholder="Select Location"
                                    errors={errors.Location}
                                    options={locationOptions}
                                />
                            </Form.Group>
                            <Form.Group>
                                <label style={{ fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                                    Status
                                </label>
                                <StatusGroup>
                                    <FormCheckBoxField
                                        control={control}
                                        name="StatusActive"
                                        label="Active"
                                        errors={errors.StatusActive}
                                    />
                                    <FormCheckBoxField
                                        control={control}
                                        name="StatusUnderMaintenance"
                                        label="Under Maintenance"
                                        errors={errors.StatusUnderMaintenance}
                                    />
                                    <FormCheckBoxField
                                        control={control}
                                        name="StatusInactive"
                                        label="Inactive"
                                        errors={errors.StatusInactive}
                                    />
                                    <FormCheckBoxField
                                        control={control}
                                        name="StatusDisposed"
                                        label="Disposed"
                                        errors={errors.StatusDisposed}
                                    />
                                </StatusGroup>
                            </Form.Group>
                            <div className="mt-3">
                                <AppPrimaryButton
                                    onClick={handleSubmit(submit)}
                                    loading={loading}
                                    fluid
                                >
                                    Search
                                </AppPrimaryButton>
                            </div>
                        </Form>
                    </SearchCriteriaSection>
                </div>
            </MobileMode>
        </>
    )
}
